import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Stethoscope, Zap, DollarSign, Palette, BarChart3 } from "lucide-react";
import type { MetaAiAnalysis } from "@shared/schema";

const typeIcons: Record<string, any> = {
  health_check: Stethoscope,
  anomaly_detection: Zap,
  budget_recommendation: DollarSign,
  creative_performance: Palette,
  weekly_summary: BarChart3,
};

const typeLabels: Record<string, string> = {
  health_check: "Health Check",
  anomaly_detection: "Anomaly Detection",
  budget_recommendation: "Budget Optimizer",
  creative_performance: "Creative Performance",
  weekly_summary: "Weekly Summary",
};

const severityColors: Record<string, string> = {
  positive: "border-l-green-500",
  info: "border-l-blue-500",
  warning: "border-l-yellow-500",
  critical: "border-l-red-500",
};

const gradeColors: Record<string, string> = {
  A: "bg-green-100 text-green-700",
  B: "bg-blue-100 text-blue-700",
  C: "bg-yellow-100 text-yellow-700",
  D: "bg-orange-100 text-orange-700",
  F: "bg-red-100 text-red-700",
};

export function AnalysisCard({ analysis }: { analysis: MetaAiAnalysis }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = typeIcons[analysis.analysisType] || BarChart3;

  let parsed: any = {};
  try {
    parsed = JSON.parse(analysis.analysis);
  } catch {}

  const date = analysis.createdAt ? new Date(analysis.createdAt).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "";

  return (
    <Card className={`border-l-4 ${severityColors[analysis.severity || "info"]}`} data-testid={`analysis-card-${analysis.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5 text-gray-500" />
            <div>
              <p className="font-medium text-sm text-gray-900">
                {typeLabels[analysis.analysisType] || analysis.analysisType}
                {parsed.overallGrade && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-semibold ${gradeColors[parsed.overallGrade] || "bg-gray-100"}`}>
                    {parsed.overallGrade}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500">{date}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} data-testid={`analysis-toggle-${analysis.id}`}>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {analysis.summary && (
          <p className="text-sm text-gray-600 mt-2">{analysis.summary}</p>
        )}

        {expanded && (
          <div className="mt-4 space-y-4 text-sm">
            {parsed.topFindings && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Key Findings</h4>
                <div className="space-y-2">
                  {parsed.topFindings.map((f: any, i: number) => (
                    <div key={i} className={`p-3 rounded-lg ${f.type === "positive" ? "bg-green-50" : f.type === "critical" ? "bg-red-50" : "bg-yellow-50"}`}>
                      <p className="font-medium">{f.title}</p>
                      <p className="text-gray-600 mt-1">{f.detail}</p>
                      {f.recommendation && <p className="text-indigo-600 mt-1 text-xs">Recommendation: {f.recommendation}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {parsed.anomalies && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anomalies</h4>
                <div className="space-y-2">
                  {parsed.anomalies.map((a: any, i: number) => (
                    <div key={i} className={`p-3 rounded-lg ${a.severity === "critical" ? "bg-red-50" : a.severity === "warning" ? "bg-yellow-50" : "bg-blue-50"}`}>
                      <p className="font-medium">{a.date} — {a.metric} {a.change}</p>
                      {a.suggestedAction && <p className="text-gray-600 mt-1">{a.suggestedAction}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {parsed.recommendations && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Budget Recommendations</h4>
                <div className="space-y-2">
                  {parsed.recommendations.map((r: any, i: number) => (
                    <div key={i} className="p-3 rounded-lg bg-indigo-50">
                      <p className="font-medium">{r.campaign}: {r.action}</p>
                      <p className="text-gray-600 mt-1">{r.reason}</p>
                      {r.currentBudget && r.suggestedBudget && (
                        <p className="text-xs text-indigo-600 mt-1">{r.currentBudget} → {r.suggestedBudget}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {parsed.topPerformers && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Top Performers</h4>
                <div className="space-y-2">
                  {parsed.topPerformers.map((p: any, i: number) => (
                    <div key={i} className="p-3 rounded-lg bg-green-50">
                      <p className="font-medium">{p.adName}</p>
                      <p className="text-gray-600 mt-1">{p.whyItWorks}</p>
                      <p className="text-xs text-green-700 mt-1">{p.keyMetric}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {parsed.wins && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Wins</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  {parsed.wins.map((w: string, i: number) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {parsed.concerns && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Concerns</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  {parsed.concerns.map((c: string, i: number) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}

            {parsed.executiveSummary && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Executive Summary</h4>
                <p className="text-gray-600 whitespace-pre-wrap">{parsed.executiveSummary}</p>
              </div>
            )}

            {parsed.trendAnalysis && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Trend</h4>
                <p className="text-gray-600">{parsed.trendAnalysis.detail}</p>
              </div>
            )}

            {parsed.nextCreativeBrief && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Next Creative Brief</h4>
                <p className="text-gray-600">{parsed.nextCreativeBrief}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
